import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { processMessageTemplate } from '../utils/messageTemplate';
import MessageTemplateDialog from '../components/MessageTemplateDialog';
import PaginatedAuditLogList from '../components/PaginatedAuditLogList';
import { get } from 'aws-amplify/api';
import { useParams, useNavigate } from 'react-router-dom';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import SymbolKey from '../components/SymbolKey';
import StatusTotals from '../components/StatusTotals';
import '../styles/management.css';
import '../utils/iconStyles.css';
import { debugLog } from '../utils/debugUtils';
import { 
  getStatusIcon, 
  getViewStatusIcon, 
  getDietaryIcon, 
  getTransportationIcon,
  getAgeCategoryIcon,
  getNotesIcon
} from '../utils/iconUtils';

// Set DEBUG to false by default for production, can be enabled for development
const DEBUG = false;

// Helper function to format guest details
const formatGuestDetails = (guest) => {
  if (!guest) return 'Unknown Guest';
  
  // Format firstName with shortName in brackets only if both are defined
  let formattedFirstName = guest.firstName || '';
  if (guest.firstName && guest.shortName) {
    formattedFirstName = `${guest.firstName} (${guest.shortName})`;
  } else if (guest.shortName) {
    formattedFirstName = guest.shortName;
  }
    
  return `${formattedFirstName} ${guest.lastName || ''}`.trim();
};


// Helper function to get a single status symbol
const getSingleStatusSymbol = (status) => {
  debugLog('getSingleStatusSymbol', { status: status });
  switch (status) {
    case true: return getStatusIcon(status);
    case false: return getStatusIcon(status);
    case 'ATTENDING': return getStatusIcon(status);
    case 'NOT_ATTENDING': return getStatusIcon(status);
    case 'MAYBE': return getStatusIcon(status);
    case 'MIXED': return getStatusIcon(status);
    case 'VIEWED': return getViewStatusIcon(status);
    case 'NOT_VIEWED': return getViewStatusIcon(status);
    default: return null;
  }
}

// Helper function to get status symbol
const getStatusSymbol = (status) => {
  return getStatusIcon(status);
};

// Helper function to determine if an invitation has been viewed
const determineViewStatus = (invitation) => {
  if (!invitation) {
    debugLog('determineViewStatus', { invitation: null, result: false });
    return false;
  }
 
  // Fall back to viewedAt if it exists and is a valid timestamp
  const result = Boolean(invitation.viewedAt);
  
  debugLog('determineViewStatus result', { 
    invitationId: invitation.id,
    viewedAt: invitation.viewedAt,
    result
  });
  
  return result;
};

// Helper function to check if dietary requirement is valid (not "No", "None", etc.)
const isValidDietaryRequirement = (requirement) => {
  debugLog('isValidDietaryRequirement', { requirement });
  
  if (!requirement) {
    debugLog('isValidDietaryRequirement', { 
      requirement: null, 
      result: false,
      reason: 'empty value'
    });
    return false;
  }
  
  const requirement_lower = requirement.toLowerCase().trim();
  const negative_values = ['no', 'none', 'n/a', 'na', '-', 'nil', 'null', 'nope'];
  
  const result = !negative_values.includes(requirement_lower);
  debugLog('isValidDietaryRequirement', { 
    requirement, 
    requirement_lower, 
    result,
    reason: result ? 'valid requirement' : 'matches negative value'
  });
  
  return result;
};


function InvitationListScreen() {
  const { eventId } = useParams();
  const [invitations, setInvitations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvitations, setSelectedInvitations] = useState([]);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [statusTotals, setStatusTotals] = useState(null);
  const [expandedDietary, setExpandedDietary] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [globalExpandDietary, setGlobalExpandDietary] = useState(false);
  const [globalExpandNotes, setGlobalExpandNotes] = useState(false);
  const navigate = useNavigate();

  const [messageTemplate, setMessageTemplate] = useState('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [previewMessages, setPreviewMessages] = useState([]);
  
  // Helper function to determine consolidated RSVP status
  const getConsolidatedRsvpStatus = useCallback((invitation) => {
    if (!invitation) {
      debugLog('getConsolidatedRsvpStatus', { invitation: null, result: null });
      return null;
    }
    const rsvpCount = invitation.guestIds ? invitation.guestIds.filter(id => rsvps && id in rsvps).length : 0
    debugLog('getConsolidatedRsvpStatus responses check', { 
      invitationId: invitation.id,
      guestCount: invitation.guestIds ? invitation.guestIds.length : 0,
      rsvpCount: rsvpCount
    });

    // Get all guest IDs from the invitation
    const allGuestIds = invitation.guestIds || [];
    
    // If there are no guests, we can't determine a status
    if (allGuestIds.length === 0) {
      debugLog('getConsolidatedRsvpStatus no guests', { 
        invitationId: invitation.id,
        guestCount: allGuestIds.length,
        result: null
      });
      return null;
    }
      
    // If there are no responses at all, return null instead of MIXED
    if (rsvpCount > 0) {
      const allStatuses = [...new Set(invitation.guestIds.map(guestId => {
        const attending = rsvps[guestId]?.attending;
        return (attending !== null && attending !== undefined && attending !== '') ? attending : '';
      }))];

      debugLog('getConsolidatedRsvpStatus statuses', {allStatuses: allStatuses })
      // if only one status return it, otherwise it is MIXED
      if (allStatuses.length === 1) {
        if (allStatuses[0] === true || allStatuses[0] === 'true') {
          return 'ATTENDING';
        }
        else if (allStatuses[0] === false || allStatuses[0] === 'false') {
          return 'NOT_ATTENDING';
        }
        else if (allStatuses[0] && allStatuses[0].length > 0) {
          return allStatuses[0].toUpperCase();
        }
        else {
          debugLog('getConsolidatedRsvpStatus single null response', {
            invitationId: invitation.id,
            invitationViewedAt: invitation.viewedAt,
            guestCount: allGuestIds.length,
            rsvpCount: rsvpCount
          });
          //  drop to catch all at end
        }
      }
      else {
        // inconsistent rsvps
        return 'MIXED'        
      }
    }
    else {
      debugLog('getConsolidatedRsvpStatus no responses', {
        invitationId: invitation.id,
        invitationViewedAt: invitation.viewedAt,
        guestCount: allGuestIds.length,
        rsvpCount: rsvpCount
      });
      //  drop to catch all at end
    }
    // catch all -- if we got here, either no responses, or the single response type is neither true nor false
    if (invitation.viewedAt && invitation.viewedAt !== '' && invitation.viewedAt !== '0') {
      return 'VIEWED'
    }
    return 'NOT_VIEWED'
  }, [rsvps]);
  
  // Helper function to get dietary requirements symbol
  const getDietarySymbol = (guest, invitation = null, guestId = null) => {
    if (!guest) {
      debugLog('getDietarySymbol', { guest: null, result: null });
      return null;
    }
    //const rsvpCount = invitation.guestIds ? invitation.guestIds.filter(id => rsvps && id in rsvps).length : 0
    if (!guest.id in rsvps) {
      debugLog('getDietarySymbol no rsvp', { guest: guest.id, result: null });
      return null;
    }
    const rsvp = rsvps[guest.id];
    if (!rsvp?.dietaryRequirements) {
      // null, false or not set      
      debugLog('getDietarySymbol false', {
        guestId: guest.id
      });      
      return false
    }
    
    const hasDietary = getDietaryIcon(rsvp, isValidDietaryRequirement) || null;

    debugLog('getDietarySymbol', {
      guestId: guest.id,
      dietaryRequirements: rsvp.dietaryRequirements || null,
      hasDietary: hasDietary
    });

    return hasDietary;
  };

  // Helper function to get transportation symbol
  const getTransportationSymbol = (guest, invitation = null, guestId = null) => {
    if (!guest) {
      debugLog('getTransportationSymbol', { guest: null, result: null });
      return null;
    }
    if (!guest.id in rsvps) {
      debugLog('getTransportationSymbol no rsvp', { guest: guest.id, result: null });
      return null;
    }
    const rsvp = rsvps[guest.id];
    if (!rsvp?.additionalOptions) {
      // null, false or not set      
      debugLog('getTransportationSymbol false', {
        guestId: guest.id
      });
      return false
    }

    const hasTransportation = Boolean(rsvp.additionalOptions && rsvp.additionalOptions !== "false")
    debugLog('getTransportationSymbol', {
      guestId: guest.id,
      additionalOptions: rsvp.additionalOptions || null,
      hasTransportation
    });
    
    // Create a modified guest object with the transportation requirements for the icon function
    /* nope why
    const guestWithTransportation = hasTransportation ? {
      ...guest,
      additionalOptions: {
        ...guest?.additionalOptions,
        transportation: hasTransportation
      }
    } : guest;
    */
    
    return getTransportationIcon(rsvp);
  };

  // Helper function to get age category symbol
  const getAgeCategorySymbol = (guest, invitation = null, guestId = null) => {
    if (!guest) {
      debugLog('getAgeCategorySymbol', { guest: null, result: null });
      return null;
    }
    if (!guest.id in rsvps) {
      debugLog('getAgeCategorySymbol no rsvp', { guest: guest.id, result: null });
      return null;
    }
    const rsvp = rsvps[guest.id];
    if (!rsvp?.ageCategory) {
      debugLog('getAgeCategorySymbol false', {
        guestId: guest.id
      });
      return false;
    }

    debugLog('getAgeCategorySymbol', {
      guestId: guest.id,
      ageCategory: rsvp.ageCategory
    });
    
    return getAgeCategoryIcon(rsvp);
  };

  // Helper function to get additional notes symbol
  const getNotesSymbol = (guest, invitation = null, guestId = null) => {
    if (!guest) {
      debugLog('getNotesSymbol', { guest: null, result: null });
      return null;
    }
    if (!guest.id in rsvps) {
      debugLog('getNotesSymbol no rsvp', { guest: guest.id, result: null });
      return null;
    }
    const rsvp = rsvps[guest.id];
    if (!rsvp?.additionalNotes) {
      debugLog('getNotesSymbol false', {
        guestId: guest.id
      });
      return false;
    }

    debugLog('getNotesSymbol', {
      guestId: guest.id,
      additionalNotes: rsvp.additionalNotes
    });
    
    return getNotesIcon(rsvp);
  };



  // Helper function to get most recent activity date
  const getMostRecentActivity = (invitation) => {
    if (!invitation) {
      debugLog('getMostRecentActivity', { invitation: null, result: null });
      return null;
    }
    
    debugLog('getMostRecentActivity', { invitationId: invitation.id });
    const dates = [];
    
    // Add invitation dates
    if (invitation.updatedAt) {
      dates.push(invitation.updatedAt);
      debugLog('getMostRecentActivity invitation updatedAt', { 
        updatedAt: invitation.updatedAt
      });
    }
    
    if (invitation.createdAt) {
      dates.push(invitation.createdAt);
      debugLog('getMostRecentActivity invitation createdAt', { 
        createdAt: invitation.createdAt
      });
    }
    
    // Add view date if exists
    // Handle both Unix timestamps (seconds since 1970) and ISO date strings
    if (invitation.viewedAt) {
      const timestamp = Number(invitation.viewedAt);
      const isNumeric = !isNaN(timestamp);
      debugLog('getMostRecentActivity viewedAt', { 
        viewedAt: invitation.viewedAt, 
        isNumeric
      });
      
      const date = new Date(isNumeric ? timestamp * 1000 : invitation.viewedAt);
      debugLog('getMostRecentActivity parsed viewedAt', { date: date.toISOString() });
      dates.push(date);
    }
    
    // Add RSVP dates if exists
    {invitation.guestIds && invitation.guestIds.map(guestId => {
      const guest = guests[guestId];
      if (!guest) {
        debugLog('Guest not found', { guestId, invitationId: invitation.id });
        return null;
      }
      
      if (rsvps && guestId in rsvps) {
        const rsvp = rsvps[guestId]

        if (rsvp.updatedAt) {
          const timestamp = Number(rsvp.updatedAt);
          const isNumeric = !isNaN(timestamp);
          debugLog('getMostRecentActivity response updatedAt', { 
            updatedAt: rsvp.updatedAt, 
            isNumeric
          });
          
          const date = new Date(isNumeric ? timestamp * 1000 : rsvp.updatedAt);
          debugLog('getMostRecentActivity parsed response updatedAt', { 
            date: date.toISOString()
          });
          dates.push(date);
        }

        if (rsvp.createdAt) {
          const timestamp = Number(rsvp.createdAt);
          const isNumeric = !isNaN(timestamp);
          debugLog('getMostRecentActivity response createdAt', { 
            updatedAt: rsvp.createdAt, 
            isNumeric
          });
          
          const date = new Date(isNumeric ? timestamp * 1000 : rsvp.createdAt);
          debugLog('getMostRecentActivity parsed response createdAt', { 
            date: date.toISOString()
          });
          dates.push(date);
        }

      }
    })}
    
    debugLog('getMostRecentActivity dates', { count: dates.length });
    
    // Filter out any invalid dates
    const validDates = dates.filter(date => date !== null && date !== undefined);
    
    if (validDates.length > 0) {
      // Convert string dates to Date objects if needed
      const dateObjects = validDates.map(d => d instanceof Date ? d : new Date(d));
      const mostRecentDate = new Date(Math.max(...dateObjects.map(d => d.getTime())));
      
      debugLog('getMostRecentActivity result', { 
        mostRecentDate: mostRecentDate.toISOString()
      });
      return mostRecentDate;
    } else {
      debugLog('getMostRecentActivity', { 
        result: null,
        reason: 'no valid dates found'
      });
      return null;
    }
  };


  // Helper function to calculate status totals
  const calculateStatusTotals = useCallback((invitationsObj, guestsObj) => {
    debugLog('calculateStatusTotals', { 
      invitationsCount: Object.keys(invitations).length,
      guestsCount: Object.keys(guests).length,
      rsvpCount: Object.keys(rsvps).length
    });
    
    // Initialize totals object
    // invitation dietary/transport may not be used
    const totals = {
      invitations: {
        ATTENDING: 0,
        NOT_ATTENDING: 0,
        MAYBE: 0,
        MIXED: 0,
        VIEWED: 0,
        NOT_VIEWED: 0,
        DIETARY: 0,
        TRANSPORTATION: 0
      },
      guests: {
        ATTENDING: 0,
        NOT_ATTENDING: 0,
        MAYBE: 0,
        VIEWED: 0,
        NOT_VIEWED: 0,
        DIETARY: 0,
        TRANSPORTATION: 0
      },
      ages: {
        Adult: 0,
        Teenager: 0,
        Child: 0,
        Baby: 0
      }
    };
    
    // Calculate invitation totals
    Object.values(invitations).forEach(invitation => {
      // Get consolidated RSVP status
      const dietaryCount = 0
      const transportCount = 0
      const rsvpCount = invitation.guestIds ? invitation.guestIds.filter(id => rsvps && id in rsvps).length : 0
      const guestCount = invitation.guestIds.length;
      const hasViewed = Boolean(invitation.viewedAt && invitation.viewedAt !== '0');
      const status = getConsolidatedRsvpStatus(invitation);
      if (status) {
        // Increment the appropriate counter
        if (totals.invitations[status] !== undefined) {
          totals.invitations[status]++;
        }
      }
      
      // Process guest-level data
      if (rsvpCount) {
        invitation.guestIds.forEach(guestId => {
          const guest = guests[guestId];
          const rsvp = rsvps[guestId];
          
          if (rsvp) {
            const guestStatus = rsvp.attending;
            if (guestStatus == true) {
              totals.guests.ATTENDING++;
            }
            else if (guestStatus == false) {
              totals.guests.NOT_ATTENDING++;
            }
            else if (hasViewed) {
              totals.guests.VIEWED++;
            }
            else {
              totals.guests.NOT_VIEWED++;
            }

            // Only count dietary, transportation, and age for attending guests
            if (guestStatus === true) {
              // Check dietary requirements
              if (getDietarySymbol(guest, invitation, guestId)) {
                totals.guests.DIETARY++;
                dietaryCount++;
              }
              
              // Check transportation requirements
              if (getTransportationSymbol(guest, invitation, guestId)) {
                totals.guests.TRANSPORTATION++;
                transportCount++;
              }

              // Count age categories
              const ageCategory = rsvp.ageCategory || 'Adult';
              if (totals.ages[ageCategory] !== undefined) {
                totals.ages[ageCategory]++;
              }
            }
          }
          // no rsvp then fall back to view state
          else if (hasViewed) {
            totals.guests.VIEWED++;
          }
          else {
            totals.guests.NOT_VIEWED++;
          }
        });
      }
      else {
        // if no rsvps then they all inherit the view state
        if (hasViewed) {
          totals.guests.VIEWED += guestCount;
        }
        else {
          totals.guests.NOT_VIEWED += guestCount;
        }
      }
    });
    
    debugLog('calculateStatusTotals result', { totals });
    return totals;
  }, [rsvps, getConsolidatedRsvpStatus]);
  
  // Initialize template from event or default
  useEffect(() => {
    if (event?.messageTemplate) {
      setMessageTemplate(event.messageTemplate);
    } else {
      setMessageTemplate("Hi {{name}}, you have been invited! Please RSVP at {{shorturl}}");
    }
  }, [event]);
    
  const safeStr = val => val || '';

  const exportToCSV = () => {
    console.log('Starting CSV export...');
    console.log(`Total invitations: ${Object.keys(invitations).length}, Selected invitations: ${selectedInvitations.length}`);

    if (!selectedInvitations || !selectedInvitations.length) {
      setSelectedInvitations(Object.keys(invitations))
      console.log(`No selected invitations, selecting all ${selectedInvitations.length} invitations`)
    }

    console.log('Selected invitations:', selectedInvitations);
    
    // Create a map to store valid guests for each invitation
    const validGuestsByInvitation = new Map();
    
    const invitationsToExport = Object.values(invitations).filter(inv => {
      console.log(`Checking invitation ${inv.id}:`);
      console.log(`- Status: ${inv.status}`);
      console.log(`- SendInvitation: ${inv.sendInvitation}`);
      
      // Helper function to check guest phone match
      const hasValidPhoneMatch = (guest, phoneData) => {
        // continue as hack to allow exporting for Messneger etc
        try {
          // Must have guest phone
          if (!guest?.phone?.trim()) {
            console.log('Guest has no phone number');
            return false;
          }

          // Normalize phone number formats and handle array/string data
          const normalizePhone = phone => (phone || '').replace(/\D/g, '');
          const guestPhone = normalizePhone(guest.phone);
          
          // Handle phone data array from sendInvitation JSON
          // Expecting format: ["0405923664", ...] or []
          if (!Array.isArray(phoneData)) {
            console.log('Invalid phone data format - expected array');
            return false;
          }

          // Both the guest phone and sendInvitation phone data need normalization
          const phoneNumbers = phoneData.map(normalizePhone);
          
          console.log('Comparing normalized phones:', {
            guestPhone,
            phoneNumbers
          });

          // Match normalized phone numbers
          const hasMatch = phoneNumbers.includes(guestPhone);
          console.log(`Phone match for guest ${guest.id}: ${hasMatch}`);
          return hasMatch;

        } catch (e) {
          console.error('Phone validation error:', e);
          return false;
        }
      };
      
      // Always validate invitation data first
      // is status ever used? check selected invitations too
      try {
        if (inv.status !== 'ACTIVE' && !selectedInvitations.includes(inv.id)) {
          console.log('- Skipping: not ACTIVE');
          return false;
        }
        
        const sendInvitationData = inv.sendInvitation ? JSON.parse(inv.sendInvitation) : {};
        if (Object.keys(sendInvitationData).length === 0) {
          console.log('- Skipping: no sendInvitation data');
          return false;
        }
        
        // Find guests with valid phone matches
        const validGuests = (inv.guestIds || [])
          .map(id => guests[id])
          .filter(guest => guest && hasValidPhoneMatch(guest, sendInvitationData[guest.id]));
          
        if (validGuests.length === 0) {
          console.log('- No valid guests found');
          return false;
        }
        
        // Store valid guests for later use
        validGuestsByInvitation.set(inv.id, validGuests);
        console.log(`- Stored ${validGuests.length} valid guests for invitation ${inv.id}`);
        
        // Extract base shortUrl from the full URL for comparison
        // URL format: https://dev4.youreinvited.au/i/Y0e2g7jl
        const shortUrlBase = inv.shortUrl.split('/i/')[1];

        // If we have selected invitations, check if this one is selected
        if (selectedInvitations.length > 0) {
          // Check shortUrl match first since selectedInvitations contains short codes
          const isSelected = selectedInvitations.includes(shortUrlBase) || 
                           selectedInvitations.includes(inv.id);
          console.log(`- Selected? ${isSelected} (shortUrl: ${shortUrlBase})`);
          return isSelected;
        }
        
        // No selections, include all valid invitations
        console.log('- No selection filter, including valid invitation');
        return true;
      } catch (e) {
        console.error('Error parsing sendInvitation for invitation:', inv.id, e);
        return false;
      }
    });
    console.log(`Filtered invitations to export: ${invitationsToExport.length}`);

    // Group by guest 'who' field
    const whoGroups = {};
    
    invitationsToExport.forEach(invitation => {
      console.log(`\nProcessing invitation ID: ${invitation.id}`);
      
      // Get guest IDs from the invitation
      const guestIds = invitation.guestIds || [];
      console.log(`Guest IDs found: ${guestIds.length}`);
      
      if (guestIds.length > 0) {
        // Get previously validated guests that we stored during filtering
        const invitationGuests = validGuestsByInvitation.get(invitation.id) || [];
        console.log(`Retrieved ${invitationGuests.length} validated guests for invitation ${invitation.id}`);
        
        // Format guest list for message
        const formatName = (guest) => {
          if (guest.firstName && guest.shortName) {
            return `${guest.firstName} (${guest.shortName})`;
          } else if (guest.shortName) {
            return guest.shortName;
          } else {
            return guest.firstName || '';
          }
        };
        
        const guestList = invitationGuests.length === 1 ? 
          formatName(invitationGuests[0]) :
          invitationGuests.length === 2 ?
          `${formatName(invitationGuests[0])} & ${formatName(invitationGuests[1])}` :
          invitationGuests.map((guest, index, arr) => 
            formatName(guest) + (index === arr.length - 1 ? '' : index === arr.length - 2 ? ' and ' : ', ')
          ).join('');
        
        console.log(`Formatted guest list: "${guestList}"`);

        // Create message for each guest that should receive an invitation
        console.log('\nProcessing individual guests for messages:');
        invitationGuests.forEach(guest => {
          console.log(`\nProcessing guest details:`);
          console.log(`- ID: ${guest.id}`);
          console.log(`- Name: ${guest.firstName} ${guest.lastName}`);
          console.log(`- Phone: ${guest.phone || 'Not provided'}`);
          
          // Get the 'who' field from the guest data, default to 'unassigned' if not present
          const who = guest.who || 'unassigned';
          console.log(`- Who: ${who}`);
          
          if (!whoGroups[who]) {
            whoGroups[who] = [];
            console.log(`Created new who group for: ${who}`);
          }
          
          // Guests in invitationGuests are already validated to have matching phone numbers
          console.log('\nProcessing message template with data:');
          console.log('Template:', messageTemplate);
          console.log('Guest data:', { ...guest, guestList, shorturl: invitation.shortUrl });
          
          // Prepare guest data for template
          const guestData = {
            name: safeStr(guest.shortName || guest.firstName || `${guest.firstName || ''} ${guest.lastName || ''}`.trim()),
            firstName: safeStr(guest.firstName),
            shortName: safeStr(guest.shortName),
            lastName: safeStr(guest.lastName),
            guestList: safeStr(guestList),
            shorturl: safeStr(invitation.shortUrl)
          };
          
          // Make sure we have at least a name
          if (!guestData.name) {
            guestData.name = 'Guest';
          }
          
          console.log('Template data:', guestData);
          
          // Directly construct message using template and data
          let message = messageTemplate;
          Object.entries(guestData).forEach(([key, value]) => {
            message = message.replace(new RegExp(`{{${key}}}`, 'gi'), value);
          });
          
          console.log('Generated message:', message);
          console.log('Adding message to who group');
          
          // Get consolidated RSVP status for the invitation
          const rsvpStatus = getConsolidatedRsvpStatus(invitation);
          
          // All guests in invitationGuests have valid phone numbers
          whoGroups[who].push({
            phoneNumber: guest.phone,
            message: message,
            rsvpStatus: rsvpStatus
          });
        });
      }
    });

    // Export a separate CSV for each who group
    console.log('\nPreparing CSV exports...');
    Object.entries(whoGroups).forEach(([who, messages]) => {
      console.log(`\nCreating CSV for who group: ${who}`);
      console.log(`Number of messages to export: ${messages.length}`);
      
      const header = 'to,message,rsvp_status\n';
      const csvContent = messages.map(({phoneNumber, message, rsvpStatus}) => 
        `${phoneNumber},"${message.replace(/"/g, '""')}","${rsvpStatus || ''}"`
      ).join('\n');
      console.log(`CSV content length: ${csvContent.length} characters`);

      const blob = new Blob([header + csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invitations_${who}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  };

  const generatePreviewMessages = () => {
    // Get up to 3 random invitations for preview
    const previewInvitations = Object.values(invitations)
      .filter(inv => selectedInvitations.length === 0 || selectedInvitations.includes(inv.id))
      .slice(0, 3);
    
    return previewInvitations.map(invitation => {
      // Get the first guest ID from the invitation
      const guestId = invitation.guestIds?.[0];
      // Find the corresponding guest object
      const guest = guestId ? guests[guestId] : null;
      
      if (!guest) return null;
      
      const previewData = {
        name: guest.shortName || guest.firstName,
        firstName: guest.firstName,
        shortName: guest.shortName,
        lastName: guest.lastName,
        shorturl: invitation.shortUrl
      };
      
      return {
        guestName: `${guest.firstName} ${guest.lastName}`,
        message: Object.entries(previewData).reduce(
          (msg, [key, value]) => msg.replace(new RegExp(`{{${key}}}`, 'g'), value || ''),
          messageTemplate
        )
      };
    }).filter(Boolean);
  };

  const handleExportClick = () => {
    const previews = generatePreviewMessages();
    setPreviewMessages(previews);
    setShowTemplateDialog(true);
  };

  const handleTemplateConfirm = () => {
    setShowTemplateDialog(false);
    exportToCSV();
  };

  const fetchEvent = useCallback(async () => {
    try {
      debugLog('fetchEvent', { eventId });
      
      const operation = get({
        apiName: 'events',
        path: `/events/${eventId}`,
        withCredentials: true
      });
      
      const response = await operation.response;
      const { body } = response;
      const data = await body.json();
      
      debugLog('fetchEvent response', { event: data });
      
      if (!data || !data.event || !data.event?.id) {
        debugLog('fetchEvent error', { 
          error: 'Invalid response format',
          data
        });
        throw new Error('Invalid event data');
      }

      setEvent(data.event);
      setError(null);
    } catch (err) {
      debugLog('fetchEvent error', { 
        error: err.message,
        stack: err.stack
      });
      // Don't set error state here to allow the app to continue if event fetch fails
      // Just log the error
      console.error('Failed to load event details:', err);
    }
  }, [eventId]);

  const fetchInvitations = useCallback(async () => {
    setIsLoading(true);
    try {
      debugLog('fetchInvitations', { eventId });
      
      const operation = get({
        apiName: 'events',
        path: `/invitations/${eventId}`,
        withCredentials: true
      });
      
      const response = await operation.response;
      const { body } = response;
      const data = await body.json();
      
      debugLog('fetchInvitations response', { 
        invitationsCount: data.invitations?.length,
        guestsCount: data.guests?.length,
        rsvpCount: data.rsvp?.length,
      });
      
      // Validate response data
      if (!data) {
        debugLog('fetchInvitations error', { 
          error: 'Empty response',
          data
        });
        throw new Error('Empty response from server');
      }
      
      if (!Array.isArray(data.invitations)) {
        debugLog('fetchInvitations error', { 
          error: 'Invalid invitations format',
          invitations: data.invitations
        });
        throw new Error('Invalid invitations data format');
      }
      
      if (!Array.isArray(data.guests)) {
        debugLog('fetchInvitations error', { 
          error: 'Invalid guests format',
          guests: data.guests
        });
        throw new Error('Invalid guests data format');
      }

      if (!Array.isArray(data.rsvp)) {
        debugLog('fetchInvitations error', { 
          error: 'Invalid rsvp format',
          rsvp: data.rsvp
        });
        throw new Error('Invalid rsvp data format');
      }

      // Create indexed arrays for faster lookups, stored in the event object
      const indexedInvitations = data.invitations.reduce((acc, invitation) => {
        if (invitation?.id) {
          acc[invitation.id] = invitation;
        } else {
            debugLog('fetchInvitations invalid invitation', { 
              invitation,
              error: 'missing id'
            });
        }
        return acc;
      }, {});

      const indexedGuests = data.guests.reduce((acc, guest) => {
        if (guest?.id) {
          acc[guest.id] = guest;
        } else {
            debugLog('guests invalid id', { 
              guest,
              error: 'missing id'
            });
        }
        return acc;
      }, {});

      const indexedRsvp = data.rsvp.reduce((acc, rsvp) => {
        // ignore rsvp.id as it's just guest_id and event_id concatenated, and we assume event_id to be static
        if (rsvp?.guest_id) {
          acc[rsvp.guest_id] = rsvp;
        } else {
            debugLog('rsvp invalid guest_id', { 
              rsvp,
              error: 'missing id'
            });
        }
        return acc;
      }, {});

      debugLog('Created indexed arrays from JSON', {
        jsonInvitationsSize: JSON.stringify(data.invitations).length,
        jsonGuestsSize: JSON.stringify(data.guests).length,
        jsonRsvpSize: JSON.stringify(data.rsvp).length,
        indexedInvitationsSize: Object.keys(indexedInvitations).length,
        indexedGuestsSize: Object.keys(indexedGuests).length,
        indexedRsvpsSize: Object.keys(indexedRsvp).length
      });
      
      // Process RSVP data if available
      if (Object.keys(indexedRsvp).length > 0) {
        Object.values(indexedRsvp).forEach(rsvp => {
          if (!rsvp || !rsvp.guest_id || !rsvp.invitation_id) {
            debugLog('fetchInvitations invalid RSVP record', { rsvp });
            return;
          }
          
          const invitation = indexedInvitations[rsvp.invitation_id];
          if (!invitation) {
            debugLog('fetchInvitations RSVP invitation not found', { 
              guestId: rsvp.guest_id,
              invitationId: rsvp.invitation_id
            });
            return;
          }
         
          debugLog('fetchInvitations processed RSVP', { 
            guestId: rsvp.guest_id,
            invitationId: rsvp.invitation_id,
            attending: rsvp.attending
          });
        });
      } else {
        debugLog('fetchInvitations no RSVP data yet', {
          rsvpCount: data.rsvp?.length || 0
        });
      }
      
      setInvitations(indexedInvitations);
      setGuests(indexedGuests);
      setRsvps(indexedRsvp);
      setError(null);
    } catch (err) {
      debugLog('fetchInvitations error', { 
        error: err.message,
        stack: err.stack
      });
      setError('Failed to load invitations. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
      fetchInvitations();
    }
  }, [eventId, fetchEvent, fetchInvitations]);
  
  // Calculate status totals whenever invitations or guests change
  useEffect(() => {
    debugLog('calculateStatusTotals effect', { 
      invitationsCount: Object.keys(invitations).length,
      guestsCount: Object.keys(guests).length,
      rsvpCount: Object.keys(rsvps).length
    });
    
    if (Object.keys(invitations).length > 0 && Object.keys(guests).length > 0) {
      debugLog('calculateStatusTotals effect starting', {
        invitationsCount: Object.keys(invitations).length,
        guestsCount: Object.keys(guests).length,
        rsvpCount: Object.keys(rsvps).length
      });
      const totals = calculateStatusTotals(invitations, guests);
      debugLog('calculateStatusTotals effect complete', { totals });
      setStatusTotals(totals);
    } else {
      debugLog('calculateStatusTotals effect skipped', {
        reason: 'insufficient data',
        invitationsCount: Object.keys(invitations).length,
        guestsCount: Object.keys(guests).length,
        rsvpCount: Object.keys(rsvps).length
      });
    }
  }, [invitations, guests, calculateStatusTotals]);

  // sortedInvitationIds takes the invitations object keyed by id, and returns an array of ids sorted by the field and direction stated 
  // currently only supports fields updatedAt (as text containing date/time) and status (as text) and directions asc/desc
  const sortedInvitationIds = useMemo(() => {
    debugLog('sortInvitationIds', {
      sortField,
      sortDirection,
      invitationCount: Object.keys(invitations).length 
    });

    if (Object.keys(invitations).length === 0) {
      debugLog('sortInvitationIds empty object', {
        reason: 'no invitations to sort'
      });
      return [];
    }

    return Object.keys(invitations).sort((aId, bId) => {
      const a = invitations[aId];
      const b = invitations[bId];

      if (sortField === 'updatedAt') {
        // For updatedAt, use getMostRecentActivity to get the most recent date
        const aDate = getMostRecentActivity(a);
        const bDate = getMostRecentActivity(b);

        debugLog('sortInvitationIds updatedAt comparison', {
          invitationA: { id: aId, date: aDate },
          invitationB: { id: bId, date: bDate }
        });

        // Handle null dates
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;

        // Compare dates
        const aTime = aDate.getTime();
        const bTime = bDate.getTime();

        return sortDirection === 'asc' ? (aTime - bTime) : (bTime - aTime);
      }

      if (sortField === 'status') {
        // For status, use getConsolidatedRsvpStatus
        const aStatus = getConsolidatedRsvpStatus(a) || '';
        const bStatus = getConsolidatedRsvpStatus(b) || '';

        debugLog('sortInvitationIds status comparison', {
          invitationA: { id: aId, status: aStatus },
          invitationB: { id: bId, status: bStatus }
        });

        // Handle null statuses
        if (!aStatus && !bStatus) return 0;
        if (!aStatus) return 1;
        if (!bStatus) return -1;

        // Compare statuses
        const comparison = aStatus.localeCompare(bStatus);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Default case for other fields
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null values
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      // Convert to strings for comparison
      const aString = String(aValue);
      const bString = String(bValue);

      // Compare values
      const comparison = aString.localeCompare(bString);

      debugLog('sortInvitationIds comparison', {
        field: sortField,
        direction: sortDirection,
        valueA: aString,
        valueB: bString,
        comparison: comparison,
        result: sortDirection === 'asc' ? comparison : -comparison
      });

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [invitations, sortField, sortDirection, getConsolidatedRsvpStatus]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="invitation-list">
      <div className="controls">
        <button 
          onClick={() => navigate(`/events/${eventId}/invitations/create`)}
          className="button button--primary"
        >
          Create New Invitation
        </button>
        <button 
          onClick={handleExportClick}
          className="button button--secondary"
        >
          Export Selected to CSV
        </button>
        
        <MessageTemplateDialog
          open={showTemplateDialog}
          onClose={() => setShowTemplateDialog(false)}
          onConfirm={handleTemplateConfirm}
          messageTemplate={messageTemplate}
          onTemplateChange={setMessageTemplate}
          previewMessages={previewMessages}
        />
      </div>

      {/* Status Totals Component */}
      {statusTotals && <StatusTotals totals={statusTotals} />}

      {/* Symbol Key Component */}
      <SymbolKey 
        onDietaryToggle={() => {
          if (!globalExpandDietary) {
            setExpandedDietary({});
          }
          setGlobalExpandDietary(!globalExpandDietary);
        }}
        onNotesToggle={() => {
          if (!globalExpandNotes) {
            setExpandedNotes({});
          }
          setGlobalExpandNotes(!globalExpandNotes);
        }}
        isDietaryExpanded={globalExpandDietary}
        isNotesExpanded={globalExpandNotes}
      />

      <div className="table-container">
        {!invitations || Object.keys(invitations).length === 0 ? (
          <p>No invitations found. Create some invitations to get started!</p>
        ) : (
          <table className="invitation-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedInvitations.length === Object.keys(invitations).length}
                    onChange={(e) => {
                      setSelectedInvitations(
                        e.target.checked ? Object.keys(invitations) : []
                      );
                    }}
                  />
                </th>
                <th onClick={() => {
                  setSortDirection(
                    sortField === 'status' && sortDirection === 'asc' ? 'desc' : 'asc'
                  );
                  setSortField('status');
                }}>
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Guests</th>
                <th onClick={() => {
                  setSortDirection(
                    sortField === 'updatedAt' && sortDirection === 'asc' ? 'desc' : 'asc'
                  );
                  setSortField('updatedAt');
                }}>
                  Last Updated {sortField === 'updatedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvitationIds.map(invitationId => {
                const invitation = invitations[invitationId];
                const consolidatedRsvp = getConsolidatedRsvpStatus(invitation)
                debugLog('consolidatedRsvp', { invitationId: invitation.id, consolidatedRsvp: consolidatedRsvp });
                return (
                <tr key={invitationId} className="invitation-row">
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedInvitations.includes(invitationId)}
                      onChange={(e) => {
                        setSelectedInvitations(
                          e.target.checked
                            ? [...selectedInvitations, invitationId]
                            : selectedInvitations.filter(id => id !== invitationId)
                        );
                      }}
                    />
                  </td>
                  <td className="status-column">
                    <div className="status-symbols" data-testid="status-symbols">
                      {getSingleStatusSymbol(consolidatedRsvp)}
                    </div>
                  </td>
                  <td className="guests-column">
                    {invitation.guestIds && invitation.guestIds.map(guestId => {
                      const guest = guests[guestId];
                      if (!guest) {
                        debugLog('Guest not found', { guestId, invitationId: invitation.id });
                        return null;
                      }
                      let rsvp = null;
                      if (guestId in rsvps) {
                        rsvp = rsvps[guestId]
                      }
                      // Only check for attending status in rsvp table
                      let attending = null;
                      if (rsvp && rsvp?.attending !== undefined) {
                        attending = rsvp.attending;
                      } else if (consolidatedRsvp == 'VIEWED' || consolidatedRsvp == 'NOT_VIEWED') {
                        attending = consolidatedRsvp;
                      } else if (invitation.viewedAt && invitation.viewedAt !== '' && invitation.viewedAt !== '0') {
                        attending = 'VIEWED'
                      } else {
                        attending = 'NOT_VIEWED'
                      }

                      // Convert boolean attending values to strings
                      let attendingClass = '';
                      if (attending === true) {
                        attending = 'ATTENDING';
                        attendingClass = 'attending';
                      } else if (attending === false) {
                        attending = 'NOT_ATTENDING';
                        attendingClass = 'not-attending';
                      } else if (attending) {
                        // If it's already a string, use toLowerCase
                        attendingClass = attending.toLowerCase();
                      }

                      return (
                        <div key={guestId} className="guest-detail">
                          <span className="guest-name" data-testid="guest-name">
                            {formatGuestDetails(guest)}
                          </span>
                          <span className="guest-indicators">
                            {(() => {
                              return attending ? (
                                <span className={`response-indicator ${attendingClass}`}>
                                  {getStatusSymbol(attending)}
                                </span>
                              ) : null;
                            })()}
                            {guest && getDietarySymbol(guest, invitation, guestId) && (
                              <span 
                                className="dietary-indicator" 
                                title={rsvp.dietaryRequirements} 
                                data-testid="dietary-indicator"
                                onClick={() => setExpandedDietary(prev => ({ ...prev, [guestId]: !prev[guestId] }))}
                                style={{ cursor: 'pointer' }}
                              >
                                {getDietarySymbol(guest, invitation, guestId)}
                                {expandedDietary[guestId] && !globalExpandDietary && (
                                  <span className="expanded-text">{rsvp.dietaryRequirements}</span>
                                )}
                                {globalExpandDietary && !expandedDietary[guestId] && (
                                  <span className="inline-text" title={rsvp.dietaryRequirements}>{rsvp.dietaryRequirements}</span>
                                )}
                              </span>
                            )}
                            {guest && getTransportationSymbol(guest, invitation, guestId) && (
                              <span className="transport-indicator" title="Requested transportation" data-testid="transport-indicator">
                                {getTransportationSymbol(guest, invitation, guestId)}
                              </span>
                            )}
                            {guest && getAgeCategorySymbol(guest, invitation, guestId) && (
                              <span className="age-indicator" title={`Age: ${rsvp?.ageCategory}`} data-testid="age-indicator">
                                {getAgeCategorySymbol(guest, invitation, guestId)}
                              </span>
                            )}
                            {guest && getNotesSymbol(guest, invitation, guestId) && (
                              <span 
                                className="notes-indicator" 
                                title={rsvp?.additionalNotes} 
                                data-testid="notes-indicator"
                                onClick={() => setExpandedNotes(prev => ({ ...prev, [guestId]: !prev[guestId] }))}
                                style={{ cursor: 'pointer' }}
                              >
                                {getNotesSymbol(guest, invitation, guestId)}
                                {expandedNotes[guestId] && !globalExpandNotes && (
                                  <span className="expanded-text">{rsvp?.additionalNotes}</span>
                                )}
                                {globalExpandNotes && !expandedNotes[guestId] && (
                                  <span className="inline-text" title={rsvp?.additionalNotes}>{rsvp?.additionalNotes}</span>
                                )}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </td>
                  <td className="date-column">
                    {(() => {
                      const mostRecentDate = getMostRecentActivity(invitation);
                      return mostRecentDate ? (
                        <>
                          <span className="time-ago">{formatTimeAgo(mostRecentDate)}</span>
                          <div className="timestamp-detail">
                            {formatDateTime(mostRecentDate)}
                          </div>
                        </>
                      ) : 'Never'
                    })()}
                  </td>
                  <td className="actions-column">
                    <button 
                      onClick={() => navigate(`/events/${eventId}/invitations/${invitation.id}`)}
                      className="button button--secondary"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => navigate(`/events/${eventId}/invitations/${invitation.id}/edit`)}
                      className="button button--secondary"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              )}
            )}
            </tbody>
          </table>
        )}
      {!isLoading && (
        <div className="audit-logs mt-8">
          <PaginatedAuditLogList
            resourceId={eventId}
            resourceType="event"
            viewType="invitations"
            pageSize={10}
          />
        </div>
      )}
      </div>
    </div>
  );
}

export default InvitationListScreen;
